import React, { useEffect, useState } from "react";
import { closeModal } from "../../master/Master";
import { Link } from "react-router-dom";
import { formatToDate } from "../../master/FormatToDateTime";

/* ─── Helper: split a batch's fee details into "current" vs "previous session" ── */
function splitFeeDetails(details) {
  if (!Array.isArray(details) || details.length === 0) return null;

  const prevItem = details.find((d) => d.fee_type === "Previous Session Fees");
  const currentItems = details.filter(
    (d) => d.fee_type !== "Previous Session Fees",
  );
  const sum = (arr, key) => arr.reduce((s, i) => s + Number(i?.[key] || 0), 0);

  return {
    current: {
      total: sum(currentItems, "actual_amount"),
      discount: sum(currentItems, "discount"),
      final: sum(currentItems, "final_amount"),
      paid: sum(currentItems, "paid_amount"),
      due: sum(currentItems, "due_amount"),
    },
    previous: prevItem
      ? {
        total: Number(prevItem.final_amount ?? prevItem.actual_amount ?? 0),
        paid: Number(prevItem.paid_amount || 0),
        due: Number(prevItem.due_amount || 0),
      }
      : null,
  };
}

/* ─── Small helper: premium stat card (single value) ─────────────────── */
function StatCard({ icon, label, value, color, accent, suffix = "" }) {
  return (
    <div className="col">
      <div
        className="card h-100 border-0 shadow-sm"
        style={{ borderLeft: `4px solid ${accent}`, borderRadius: "12px" }}
      >
        <div className="card-body p-3 d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              background: `${accent}1A`,
            }}
          >
            <i className={`${icon} ri-22px`} style={{ color: accent }} />
          </div>
          <div className="overflow-hidden">
            <div
              className="text-muted text-nowrap"
              style={{ fontSize: "0.78rem" }}
            >
              {label}
            </div>
            <h5 className={`mb-0 fw-bold text-nowrap ${color}`}>
              {value}
              {suffix}
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Stat card variant: Total Fee with strikethrough + discount ─────── */
function TotalFeeStatCard({
  icon,
  label,
  gross,
  net,
  discount,
  accent,
  color,
}) {
  const hasDiscount = discount > 0;
  return (
    <div className="col">
      <div
        className="card h-100 border-0 shadow-sm"
        style={{ borderLeft: `4px solid ${accent}`, borderRadius: "12px" }}
      >
        <div className="card-body p-3 d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              background: `${accent}1A`,
            }}
          >
            <i className={`${icon} ri-22px`} style={{ color: accent }} />
          </div>
          <div className="overflow-hidden">
            <div
              className="text-muted text-nowrap"
              style={{ fontSize: "0.78rem" }}
            >
              {label}
            </div>
            {hasDiscount && (
              <div
                className="text-muted text-decoration-line-through text-nowrap"
                style={{ fontSize: "0.8rem" }}
              >
                ₹{gross.toLocaleString()}
              </div>
            )}
            <h5 className={`mb-0 fw-bold text-nowrap ${color}`}>
              ₹{net.toLocaleString()}
              {hasDiscount && (
                <small
                  className="text-warning fw-semibold ms-1"
                  style={{ fontSize: "0.68rem" }}
                >
                  (₹{discount.toLocaleString()} off)
                </small>
              )}
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Small helper: labelled info badge ──────────────────────────────── */
function InfoBadge({ icon, label, value, bg, color }) {
  return (
    <span
      className="badge rounded-pill d-inline-flex align-items-center gap-1 px-3 py-2"
      style={{ background: bg, color, fontWeight: 500, fontSize: "0.8rem" }}
    >
      <i className={`${icon} ri-16px`} />
      {label}: <b>{value || "-"}</b>
    </span>
  );
}

/* ─── Payable-fee callout block (crossed total → final + previous session) ── */
function PayableFeeBlock({ current, previous, align = "end" }) {
  const hasDiscount = current.discount > 0;
  return (
    <div className={`text-lg-${align}`}>
      {hasDiscount && (
        <div
          className={`text-decoration-line-through text-muted fs-6 text-lg-${align}`}
        >
          ₹{current.total.toLocaleString()}
        </div>
      )}
      <div
        className={`d-flex flex-wrap align-items-baseline gap-1 justify-content-lg-${align}`}
      >
        <h4 className="fw-bold text-primary mb-0">
          ₹{current.final.toLocaleString()}
        </h4>
        {hasDiscount && (
          <span className="badge bg-label-warning">
            ₹{current.discount.toLocaleString()} discount
          </span>
        )}
        {previous && (
          <span className="badge bg-label-secondary">
            + Previous Session Fees (₹{previous.total.toLocaleString()})
          </span>
        )}
      </div>
    </div>
  );
}

export default function BasicDetailsV4({ data, setFilters, handleFunction }) {
  const [modalData, setModalData] = useState({});
  const [summary, setSummary] = useState({});

  useEffect(() => {
    if (data && data?.course_and_fees) {
      setSummary({
        totalFee: data.course_and_fees.reduce(
          (sum, item) => sum + Number(item?.total_fee || 0),
          0,
        ),
        totalDiscount: data.course_and_fees.reduce(
          (sum, item) => sum + Number(item?.total_discount || 0),
          0,
        ),
        finalFee: data.course_and_fees.reduce(
          (sum, item) => sum + Number(item?.final_fee || 0),
          0,
        ),
        totalPaid: data.course_and_fees.reduce(
          (sum, item) => sum + Number(item?.total_paid || 0),
          0,
        ),
        totalDue: data.course_and_fees.reduce(
          (sum, item) => sum + Number(item?.total_due || 0),
          0,
        ),
        totalAttendance: data?.attendance_report?.total || 0,
        totalPresent: data?.attendance_report?.present || 0,
        totalAbsent: data?.attendance_report?.absent || 0,
        attendancePercent: data?.attendance_report?.total
          ? (data.attendance_report.present / data.attendance_report.total) *
          100
          : 0,
      });
    }
  }, [data]);

  const feeInfo = data?.course_and_fee_info;

  // ── Test performance derived values (top-level, used by both the stat card
  //    row and the detail card below) ─────────────────────────────────────
  const testStats = feeInfo?.tests || {
    total: 0,
    attempt: 0,
    un_attempt: 0,
    total_marks: 0,
    obtained_total: 0,
  };
  const testAvgPercent =
    Number(testStats?.total_marks) > 0
      ? (Number(testStats.obtained_total) / Number(testStats.total_marks)) *
      100
      : 0;

  const openFeeModal = (info) => {
    setModalData(info || {});
    const modal = new window.bootstrap.Modal(
      document.getElementById("CourseNFeeView"),
    );
    modal.show();
  };

  return (
    <>
      <div className="row row-cols-2 row-cols-md-5 g-3 mb-4">
        <TotalFeeStatCard
          icon="ri-wallet-3-line"
          label="Total Fee"
          gross={summary?.totalFee || 0}
          net={summary?.finalFee || 0}
          discount={summary?.totalDiscount || 0}
          accent="#696cff"
          color="text-primary"
        />
        <StatCard
          icon="ri-checkbox-circle-line"
          label="Total Receive"
          value={`₹${(summary?.totalPaid || 0).toLocaleString()}`}
          accent="#28a745"
          color="text-success"
        />
        <StatCard
          icon="ri-error-warning-line"
          label="Total Due"
          value={`₹${(summary?.totalDue || 0).toLocaleString()}`}
          accent="#dc3545"
          color="text-danger"
        />
        <StatCard
          icon="ri-calendar-check-line"
          label="Attendance"
          value={(summary?.attendancePercent || 0).toFixed(1)}
          suffix="%"
          accent="#17a2b8"
          color="text-info"
        />
        <StatCard
          icon="ri-file-chart-2-line"
          label="Test Average"
          value={testAvgPercent.toFixed(2)}
          suffix="%"
          accent="#6f42c1"
          color="text-primary"
        />
      </div>
      <div className="card rounded-3">
        <div className="card-body">
          <div className="row align-items-center g-3 mb-3">
            <div className="col-md">
              <h5 className="mb-1 fw-bold">Student Performance Dashboard</h5>
              <small className="text-muted">
                Course, Fee, Attendance &amp; Test Analytics
              </small>
            </div>

            <div className="col-md-auto">
              <div className="form-floating form-floating-outline">
                <select
                  className="form-select"
                  value={data?.course_and_fee_info?.id || ""}
                  onChange={(e) => {
                    setFilters((prev) => ({
                      ...prev,
                      student_batch_n_fee_id: Number(e.target.value),
                    }));
                  }}
                >
                  <option value="">Select Batch</option>
                  {data?.course_and_fees?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c?.batch?.title} ({c?.course?.title})
                    </option>
                  ))}
                </select>
                <label>Batch</label>
              </div>
            </div>
          </div>

          <div className="">
            {feeInfo &&
              (() => {
                const split = splitFeeDetails(feeInfo?.student_fee_details);
                const current = split
                  ? split.current
                  : {
                    total: Number(feeInfo?.total_fee || 0),
                    discount: Number(feeInfo?.total_discount || 0),
                    final: Number(feeInfo?.final_fee || 0),
                    paid: Number(feeInfo?.total_paid || 0),
                    due: Number(feeInfo?.total_due || 0),
                  };
                const previous = split?.previous || null;
                const totalDue = current.due + (previous?.due || 0);
                const feePaidPercent =
                  Number(feeInfo?.final_fee || 0) > 0
                    ? Math.min(
                      100,
                      (Number(feeInfo.total_paid) / Number(feeInfo.final_fee)) *
                      100,
                    )
                    : 0;
                const attendPercent = feeInfo?.attendance?.total
                  ? (feeInfo.attendance.present / feeInfo.attendance.total) * 100
                  : 0;

                return (
                  <div
                    className="card border shadow-none mb-4"
                    style={{ borderRadius: "12px" }}
                  >
                    <div className="card-body p-4">
                      <div className="row g-4">
                        {/* Course / batch / session badges */}
                        <div className="col-12 col-lg-7 d-flex flex-wrap align-items-start gap-2">
                          <InfoBadge
                            icon="ri-book-open-line"
                            label="Course"
                            value={feeInfo?.course?.title}
                            bg="#e7e7ff"
                            color="#696cff"
                          />
                          <InfoBadge
                            icon="ri-team-line"
                            label="Batch"
                            value={feeInfo?.batch?.title}
                            bg="#ddf6e8"
                            color="#28a745"
                          />
                          <InfoBadge
                            icon="ri-calendar-2-line"
                            label="Session"
                            value={feeInfo?.session_year?.session}
                            bg="#d9f1f8"
                            color="#17a2b8"
                          />
                          {feeInfo?.roll_number && (
                            <InfoBadge
                              icon="ri-hashtag"
                              label="Roll No"
                              value={feeInfo?.roll_number}
                              bg="#fdeedd"
                              color="#f59e0b"
                            />
                          )}
                          {feeInfo?.id && (
                            <InfoBadge
                              icon="ri-hashtag"
                              label="Id"
                              value={feeInfo?.id}
                              bg="#fdeedd"
                              color="#f59e0b"
                            />
                          )}
                        </div>



                        {/* Payable fee callout + view button */}
                        <div className="col-12 col-lg-5 text-lg-end">
                          <div className="d-flex justify-content-between justify-content-lg-end align-items-baseline gap-2">
                            <span className="text-muted small text-uppercase fw-semibold">
                              Payable Fee
                            </span>
                            <i
                              className="ri-eye-line ri-20px cursor-pointer text-primary"
                              role="button"
                              title="View batch details"
                              onClick={() => openFeeModal(feeInfo)}
                            />
                          </div>
                          <PayableFeeBlock
                            current={current}
                            previous={previous}
                          />
                        </div>


                        {/* Fee progress */}
                        <div className="col-12">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="small text-muted">
                              Received ₹
                              {Number(feeInfo?.total_paid || 0).toLocaleString()}{" "}
                              of ₹
                              {Number(feeInfo?.final_fee || 0).toLocaleString()}
                            </span>
                            <span className="small fw-semibold text-success">
                              {feePaidPercent.toFixed(0)}%
                            </span>
                          </div>
                          <div
                            className="progress"
                            style={{ height: "8px", borderRadius: "6px" }}
                          >
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{ width: `${feePaidPercent}%` }}
                              aria-valuenow={feePaidPercent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </div>


                        <div className="col-12">
                          <hr className="my-1" />
                        </div>


                        {/* Fee breakdown mini stats (current session) */}
                        <div className="col-6 col-md-3">
                          <div className="text-muted small text-nowrap mb-1">
                            Total Fee
                          </div>
                          <div className="fw-bold text-dark">
                            ₹{current.total.toLocaleString()}
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="text-muted small text-nowrap mb-1">
                            Discount
                          </div>
                          <div className="fw-bold text-warning">
                            ₹{current.discount.toLocaleString()}
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="text-muted small text-nowrap mb-1">
                            Receive Fee
                          </div>
                          <div className="fw-bold text-success">
                            ₹{current.paid.toLocaleString()}
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="text-muted small text-nowrap mb-1">
                            Due Fee
                          </div>
                          <div className="fw-bold text-danger">
                            ₹{totalDue.toLocaleString()}
                          </div>
                        </div>

                        {/* Previous session breakdown, if any */}
                        {previous && (
                          <>
                            <div className="col-12">
                              <hr className="my-1" />
                            </div>
                            <div className="col-12">
                              <div className="small text-uppercase fw-semibold text-muted mb-2">
                                Previous Session Fees
                              </div>
                            </div>
                            <div className="col-6 col-md-4">
                              <div className="text-muted small text-nowrap mb-1">
                                Total
                              </div>
                              <div className="fw-bold text-dark">
                                ₹{previous.total.toLocaleString()}
                              </div>
                            </div>
                            <div className="col-6 col-md-4">
                              <div className="text-muted small text-nowrap mb-1">
                                Receive Fee
                              </div>
                              <div className="fw-bold text-success">
                                ₹{previous.paid.toLocaleString()}
                              </div>
                            </div>
                            <div className="col-6 col-md-4">
                              <div className="text-muted small text-nowrap mb-1">
                                Due
                              </div>
                              <div className="fw-bold text-danger">
                                ₹{previous.due.toLocaleString()}
                              </div>
                            </div>
                          </>
                        )}

                        <div className="col-12">
                          <hr className="my-1" />
                        </div>

                        {/* Attendance mini stats */}
                        <div className="col-12 col-md-8">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">
                              Batch Attendance
                            </span>
                            <span className="small fw-semibold text-info">
                              {attendPercent.toFixed(1)}%
                            </span>
                          </div>
                          <div
                            className="progress"
                            style={{ height: "8px", borderRadius: "6px" }}
                          >
                            <div
                              className="progress-bar bg-info"
                              role="progressbar"
                              style={{ width: `${attendPercent}%` }}
                              aria-valuenow={attendPercent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Present
                          </div>
                          <div className="fw-bold text-dark">
                            {feeInfo?.attendance?.present ?? 0}
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Absent
                          </div>
                          <div className="fw-bold text-warning">
                            {feeInfo?.attendance?.absent ?? 0}
                          </div>
                        </div>

                        {/* ── NEW: Test Performance section ───────────────── */}
                        <div className="col-12">
                          <hr className="my-1" />
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-muted small text-uppercase fw-semibold">
                              Test Performance
                            </span>
                            <span className="badge rounded-pill bg-label-primary fw-bold">
                              {testAvgPercent.toFixed(2)}% Avg
                            </span>
                          </div>
                          <div
                            className="progress mb-2"
                            style={{ height: "8px", borderRadius: "6px" }}
                          >
                            <div
                              className="progress-bar bg-primary"
                              role="progressbar"
                              style={{ width: `${Math.min(100, testAvgPercent)}%` }}
                              aria-valuenow={testAvgPercent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Total Test
                          </div>
                          <div className="fw-bold text-dark">
                            {testStats?.total ?? 0}
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Attempted
                          </div>
                          <div className="fw-bold text-info">
                            {testStats?.attempt ?? 0}
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Un Attempt
                          </div>
                          <div className="fw-bold text-warning">
                            {testStats?.un_attempt ?? 0}
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Total Marks
                          </div>
                          <div className="fw-bold text-dark">
                            {testStats?.total_marks ?? 0}
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Obtained
                          </div>
                          <div className="fw-bold text-success">
                            {testStats?.obtained_total ?? 0}
                          </div>
                        </div>
                        <div className="col-6 col-md-2">
                          <div className="text-muted small text-nowrap mb-1">
                            Average
                          </div>
                          <div className="fw-bold text-primary">
                            {testAvgPercent.toFixed(2)}%
                          </div>
                        </div>
                        {/* ── END: Test Performance section ───────────────── */}
                      </div>


                    </div>
                  </div>

                );
              })()}
          </div>
        </div>
      </div>

      {/* 🔷 Batch details modal */}
      <div
        className="modal fade"
        id="CourseNFeeView"
        tabIndex="-1"
        aria-labelledby="courseNFeeViewLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: "14px", overflow: "hidden" }}
          >
            <div className="modal-header py-3 bg-label-primary">
              <h5 className="modal-title fw-bold" id="courseNFeeViewLabel">
                Batch Details
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body p-4">
              <div className="row g-4">
                <div className="col-md-6">
                  <h6 className="text-primary fw-bold mb-3">
                    <i className="ri-book-open-line me-1" /> Course Info
                  </h6>
                  <p className="mb-2">
                    <strong>Session:</strong>{" "}
                    {modalData?.session_year?.session || "-"}
                  </p>
                  <p className="mb-2">
                    <strong>Course:</strong> {modalData?.course?.title || "-"}
                  </p>
                  <p className="mb-2">
                    <strong>Batch:</strong> {modalData?.batch?.title || "-"}
                  </p>
                  <p className="mb-0">
                    <strong>Subjects:</strong>{" "}
                    {modalData?.subjects
                      ? JSON.parse(modalData.subjects)
                        .map((item) => JSON.parse(item)?.name)
                        .join(", ")
                      : "-"}
                  </p>
                </div>

                <div className="col-md-6">
                  <h6 className="text-primary fw-bold mb-3">
                    <i className="ri-user-3-line me-1" /> Basic Info
                  </h6>
                  <p className="mb-2">
                    <strong>Roll No:</strong> {modalData?.roll_number || "-"}
                  </p>
                  <p className="mb-2">
                    <strong>Batch Enroll Date:</strong>{" "}
                    {formatToDate(modalData?.batch_enroll_date)}
                  </p>
                  <p className="mb-2">
                    <strong>RTE Status:</strong> {modalData?.rte_status || "-"}
                  </p>
                  {modalData?.previous_batch_n_fee?.batch?.title && (
                    <p className="mb-2">
                      <strong>Previous Batch:</strong>{" "}
                      {modalData.previous_batch_n_fee.batch.title}
                    </p>
                  )}
                  {modalData?.next_batch_n_fee?.batch?.title && (
                    <p className="mb-2">
                      <strong>Next Batch:</strong>{" "}
                      {modalData.next_batch_n_fee.batch.title}
                    </p>
                  )}
                  <p className="mb-2">
                    <strong>Status:</strong>{" "}
                    <span
                      className={`badge bg-label-${Number(modalData?.status) === 1
                        ? "success"
                        : Number(modalData?.status) === 5
                          ? "info"
                          : Number(modalData?.status) === 4
                            ? "warning"
                            : "secondary"
                        }`}
                    >
                      {Number(modalData?.status) === 1
                        ? "Active"
                        : Number(modalData?.status) === 5
                          ? "Finished"
                          : Number(modalData?.status) === 4
                            ? "Transferred"
                            : "Inactive"}
                    </span>
                  </p>
                  {modalData?.counselor?.name && (
                    <p className="mb-0">
                      <strong>Counselor:</strong> {modalData.counselor.name} (
                      {modalData.counselor.registration_no})
                    </p>
                  )}
                </div>

                <div className="col-md-6">
                  <h6 className="text-primary fw-bold mb-3">
                    <i className="ri-file-list-3-line me-1" /> Fee Details
                  </h6>
                  {modalData?.student_fee_details?.length ? (
                    modalData.student_fee_details.map((item) => (
                      <p className="mb-2" key={item.id}>
                        <strong>{item?.fee_type}:</strong>{" "}
                        <span className="text-success">
                          ₹{Number(item?.paid_amount || 0).toLocaleString()}
                        </span>{" "}
                        / ₹{Number(item?.final_amount || 0).toLocaleString()}
                        {item?.fee_type === "Previous Session Fees" &&
                          modalData?.previous_session_due_fees_remark && (
                            <small className="text-muted ms-1">
                              ({modalData.previous_session_due_fees_remark})
                            </small>
                          )}
                      </p>
                    ))
                  ) : (
                    <p className="text-muted mb-0">No fee details available</p>
                  )}
                </div>

                <div className="col-md-6">
                  <h6 className="text-primary fw-bold mb-3">
                    <i className="ri-money-rupee-circle-line me-1" /> Total Fee
                    Details
                  </h6>
                  <p className="mb-2">
                    <strong>Total Fee:</strong> ₹
                    {Number(modalData?.total_fee || 0).toLocaleString()}
                  </p>
                  <p className="mb-2">
                    <strong>Discount:</strong> ₹
                    {Number(modalData?.total_discount || 0).toLocaleString()}
                    {modalData?.discount_remark
                      ? ` (${modalData.discount_remark})`
                      : ""}
                  </p>
                  <p className="mb-0">
                    <strong>Payable Fee:</strong>{" "}
                    <span className="text-success">
                      ₹{Number(modalData?.total_paid || 0).toLocaleString()}
                    </span>{" "}
                    / ₹{Number(modalData?.final_fee || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="modal-footer py-3 bg-label-primary">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
              {/* <Link
                className="btn btn-info"
                onClick={() => closeModal("CourseNFeeView")}
              >
                Update Fee Detail
              </Link> */}


              <button
                type="button"
                className="btn btn-info"
              >
                Update Fee Detail
              </button>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}
